import React from 'react'
import { Radio, Select, Button } from 'antd'
import {
    AreaChartOutlined,
    PieChartOutlined,
    LineChartOutlined,
    SwapOutlined,
    ReloadOutlined,
    DownloadOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import type { RangePickerProps } from 'antd/es/date-picker'
import dayjs from 'dayjs'
import {
    FilterActions,
    FilterBar,
    FilterField,
    FilterSection,
} from '../common/FilterBar'
import { ReadonlyRangePicker } from '../common/DatePickerControls'
import './StatisticsHeader.css'
import {
    buildAllFilterOptions,
    type FilterOption,
    getAllAwareDisplayValues,
    normalizeAllAwareValues,
} from './filterSelectUtils'
import { filterRegionSelectOption } from '../../utils/regionSearch'
import { filterSearchOptionByLabel } from '../../utils/searchText'

interface StatisticsHeaderProps {
    dateRange: [dayjs.Dayjs, dayjs.Dayjs]
    dateRangeDisabled?: boolean
    chartType: string
    activeQuickRangeKey: string | null
    selectedProjectIds: number[]
    selectedRegionIds: number[]
    selectedDeviceIds: string[]
    selectedPestTypes: string[]
    projectOptions: FilterOption<number>[]
    regionOptions: FilterOption<number>[]
    deviceOptions: FilterOption<string>[]
    pestTypeOptions: FilterOption<string>[]
    onDateRangeChange: RangePickerProps['onChange']
    onChartTypeChange: (chartType: string) => void
    onQuickRangeChange: (quickRangeKey: string) => void
    onProjectChange: (projectIds: number[]) => void
    onRegionChange: (regionIds: number[]) => void
    onDeviceChange: (deviceIds: string[]) => void
    onPestTypeChange: (pestTypes: string[]) => void
    onResetFilters: () => void
    onRefresh?: () => void
    onExport: () => void
    onExportTasks?: () => void
    hasActiveFilters: boolean
    showExport: boolean
    exportDisabled?: boolean
    exportDisabledReason?: string
    exporting: boolean
    showChartTypeSelector?: boolean
}

export const StatisticsHeader: React.FC<StatisticsHeaderProps> = ({
    dateRange,
    dateRangeDisabled = false,
    chartType,
    activeQuickRangeKey,
    selectedProjectIds,
    selectedRegionIds,
    selectedDeviceIds,
    selectedPestTypes,
    projectOptions,
    regionOptions,
    deviceOptions,
    pestTypeOptions,
    onDateRangeChange,
    onChartTypeChange,
    onQuickRangeChange,
    onProjectChange,
    onRegionChange,
    onDeviceChange,
    onPestTypeChange,
    onResetFilters,
    onRefresh,
    onExport,
    onExportTasks,
    showExport,
    exportDisabled = false,
    exportDisabledReason,
    exporting,
    showChartTypeSelector = true,
}) => {
    const { t } = useTranslation(['statistics', 'common'])

    const projectSelectOptions = buildAllFilterOptions(
        t('filters.allProject'),
        projectOptions
    )
    const regionSelectOptions = buildAllFilterOptions(
        t('filters.allRegion'),
        regionOptions
    )
    const deviceSelectOptions = buildAllFilterOptions(
        t('filters.allDevice'),
        deviceOptions
    )
    const pestTypeSelectOptions = buildAllFilterOptions(
        t('filters.allPestType'),
        pestTypeOptions
    )
    const quickRangeOptions = [
        { key: 'all', label: t('filters.quickRanges.all') },
        { key: 'today', label: t('filters.quickRanges.today') },
        { key: 'yesterday', label: t('filters.quickRanges.yesterday') },
        { key: 'lastWeek', label: t('filters.quickRanges.lastWeek') },
        { key: 'lastMonth', label: t('filters.quickRanges.lastMonth') },
        { key: 'lastHalfYear', label: t('filters.quickRanges.lastHalfYear') },
        { key: 'lastYear', label: t('filters.quickRanges.lastYear') },
    ]

    return (
        <FilterBar
            className="statistics-filter-bar"
            data-testid="statistics-filter-toolbar"
        >
            <div className="statistics-filter-top-row">
                {showChartTypeSelector && (
                    <FilterSection
                        className="statistics-filter-analysis"
                        testId="statistics-filter-analysis"
                        variant="inline"
                    >
                        <Radio.Group
                            value={chartType}
                            onChange={(event) =>
                                onChartTypeChange(event.target.value)
                            }
                            buttonStyle="solid"
                            className="statistics-chart-type-group"
                        >
                            <Radio.Button value="trend">
                                <AreaChartOutlined /> {t('chartTypes.trend')}
                            </Radio.Button>
                            <Radio.Button value="distribution">
                                <PieChartOutlined /> {t('chartTypes.distribution')}
                            </Radio.Button>
                            <Radio.Button value="prediction">
                                <LineChartOutlined /> {t('chartTypes.prediction')}
                            </Radio.Button>
                            <Radio.Button value="comparison">
                                <SwapOutlined /> {t('chartTypes.comparison')}
                            </Radio.Button>
                        </Radio.Group>
                    </FilterSection>
                )}

                <FilterSection
                    className="statistics-filter-time"
                    testId="statistics-filter-time"
                >
                    <FilterField
                        className="statistics-date-filter-field"
                        label={t('filters.dateRange')}
                    >
                        <ReadonlyRangePicker
                            value={dateRange}
                            onChange={onDateRangeChange}
                            allowClear={false}
                            disabled={dateRangeDisabled}
                            className="statistics-date-range-picker"
                        />
                    </FilterField>
                    <FilterField
                        className="statistics-quick-filter-field"
                        label={t('filters.quickFilter')}
                    >
                        <div className="statistics-quick-range-group">
                            {quickRangeOptions.map((option) => (
                                <Button
                                    key={option.key}
                                    type={
                                        activeQuickRangeKey === option.key
                                            ? 'primary'
                                            : 'default'
                                    }
                                    disabled={dateRangeDisabled}
                                    onClick={() =>
                                        onQuickRangeChange(option.key)
                                    }
                                >
                                    {option.label}
                                </Button>
                            ))}
                        </div>
                    </FilterField>
                </FilterSection>
            </div>

            <div className="statistics-filter-bottom-row">
                <FilterSection
                    className="statistics-filter-conditions"
                    testId="statistics-filter-conditions"
                >
                    <FilterField label={t('filters.project')}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={getAllAwareDisplayValues(selectedProjectIds)}
                            options={projectSelectOptions}
                            placeholder={t('filters.projectPlaceholder')}
                            optionFilterProp="label"
                            filterOption={filterSearchOptionByLabel}
                            maxTagCount="responsive"
                            onChange={(value) =>
                                onProjectChange(
                                    normalizeAllAwareValues(
                                        value,
                                        selectedProjectIds
                                    )
                                )
                            }
                        />
                    </FilterField>

                    <FilterField label={t('filters.region')}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={getAllAwareDisplayValues(selectedRegionIds)}
                            options={regionSelectOptions}
                            placeholder={t('filters.regionPlaceholder')}
                            optionFilterProp="label"
                            filterOption={filterRegionSelectOption}
                            maxTagCount="responsive"
                            onChange={(value) =>
                                onRegionChange(
                                    normalizeAllAwareValues(
                                        value,
                                        selectedRegionIds
                                    )
                                )
                            }
                        />
                    </FilterField>

                    <FilterField label={t('filters.device')}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={getAllAwareDisplayValues(selectedDeviceIds)}
                            options={deviceSelectOptions}
                            placeholder={t('filters.devicePlaceholder')}
                            optionFilterProp="label"
                            filterOption={filterSearchOptionByLabel}
                            maxTagCount="responsive"
                            onChange={(value) =>
                                onDeviceChange(
                                    normalizeAllAwareValues(
                                        value,
                                        selectedDeviceIds
                                    )
                                )
                            }
                        />
                    </FilterField>

                    <FilterField label={t('filters.pestType')}>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={getAllAwareDisplayValues(selectedPestTypes)}
                            options={pestTypeSelectOptions}
                            placeholder={t('filters.pestTypePlaceholder')}
                            optionFilterProp="label"
                            filterOption={filterSearchOptionByLabel}
                            maxTagCount="responsive"
                            onChange={(value) =>
                                onPestTypeChange(
                                    normalizeAllAwareValues(
                                        value,
                                        selectedPestTypes
                                    )
                                )
                            }
                        />
                    </FilterField>
                </FilterSection>

                <FilterActions
                    className="statistics-filter-actions"
                    testId="statistics-filter-actions"
                >
                    <Button onClick={onResetFilters}>
                        {t('filters.reset')}
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={onRefresh}
                        disabled={!onRefresh}
                    >
                        {t('common:buttons.refresh')}
                    </Button>
                    {showExport && (
                        <>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={onExport}
                                disabled={exportDisabled}
                                loading={exporting}
                                title={exportDisabledReason}
                            >
                                {t('export.button')}
                            </Button>
                            {onExportTasks && (
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    onClick={onExportTasks}
                                >
                                    {t('exportTasks.button')}
                                </Button>
                            )}
                        </>
                    )}
                </FilterActions>
            </div>
        </FilterBar>
    )
}
